import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
import { useOSStore } from '../../store/useOSStore';

// ── ModuleWorkspace ───────────────────────────────────────────────────────────
// Base 3-panel layout for sport module workspaces:
//   [Slate: shrinks to w-72 when canvas open] | [Canvas: AnimatePresence] | [Uplink: 200px]

interface ModuleWorkspaceProps {
  slatePanel: React.ReactNode;
  canvas?: React.ReactNode;
  uplinkPanel: React.ReactNode;
  className?: string;
}

export function ModuleWorkspace({ slatePanel, canvas, uplinkPanel, className }: ModuleWorkspaceProps) {
  const { activeGameId } = useOSStore();
  const hasCanvas = !!activeGameId && !!canvas;

  return (
    <div className={clsx('flex flex-1 overflow-hidden min-h-0', className)}>

      {/* Slate panel — narrows when canvas is open */}
      <motion.div
        layout
        transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
        className={clsx(
          'flex flex-col border-r border-surface-4 overflow-hidden min-h-0 flex-shrink-0',
          hasCanvas ? 'w-72' : 'flex-1',
        )}
      >
        {slatePanel}
      </motion.div>

      {/* Game intelligence canvas — slides in from right */}
      <AnimatePresence initial={false}>
        {hasCanvas && (
          <motion.div
            key="game-canvas"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 16 }}
            transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
            className="flex-1 overflow-hidden min-h-0 flex flex-col border-r border-surface-4"
          >
            {canvas}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pulse uplink — always visible on large screens */}
      <div className="hidden lg:flex flex-col w-52 flex-shrink-0 overflow-hidden min-h-0">
        {uplinkPanel}
      </div>
    </div>
  );
}
